import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { ModalShell } from '@/components/ui/ModalShell'
import { morphToast } from '@/components/ui/Toast'
import { useCommitsStore } from '@/stores/commitsStore'
import { useDraftStore } from '@/stores/draftStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { projectApi } from '@/api/projectApi'
import { createCommitFromMetadata } from '@/services/projectBootstrap'
import { openPlanDialogForError } from '@/utils/openPlanDialogForError'

// ── Store-level accessor helpers ──────────────────────────────────────────────

async function getCurrentMetadata() {
  const { useDiagramStore } = await import('@/stores/diagramStore')
  const { useViewportStore } = await import('@/stores/viewportStore')
  const { useTabViewStore } = await import('@/stores/tabViewStore')
  const { useSettingsStore } = await import('@/stores/settingsStore')

  const diagram = useDiagramStore.getState()
  const viewport = useViewportStore.getState()
  const tabView = useTabViewStore.getState()
  const settings = useSettingsStore.getState()

  return editorStateToMetadata({
    diagram: {
      tables: diagram.tables,
      rels: diagram.rels,
      groups: diagram.groups,
      defaults: diagram.defaults,
      projectName: diagram.projectName,
      dialect: diagram.dialect,
    },
    viewport: { sc: viewport.sc, tx: viewport.tx, ty: viewport.ty },
    tabView: { hiddenTableIds: tabView.hiddenTableIds },
    settings: {
      defaultLineMode: settings.defaultLineMode,
      relationshipAnimationEnabled: settings.relationshipAnimationEnabled,
      showGrid: settings.showGrid,
      themePreference: settings.themePreference,
      sbCollapsed: settings.sbCollapsed,
      defaults: settings.defaults,
    },
    project: {
      name: diagram.projectName,
      description: '',
      dialect: diagram.dialect,
    },
  })
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface ConflictModalProps {
  open: boolean
  onClose: () => void
  currentCommitId: number
}

export function ConflictModal({ open, onClose, currentCommitId }: ConflictModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [showNewProjectForm, setShowNewProjectForm] = useState(false)
  const [confirmUpdate, setConfirmUpdate] = useState(false)

  const currentProjectId = useDraftStore((s) => s.currentProjectId)
  const restoreCommit = useCommitsStore((s) => s.restoreCommit)
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)

  async function handleUpdateProject() {
    if (!currentProjectId) return
    if (!confirmUpdate) { setConfirmUpdate(true); return }

    setBusy(true)
    try {
      await restoreCommit(currentProjectId, currentCommitId)
      onClose()
      morphToast.success(t('conflict.updated', 'Projeto atualizado para a versão mais recente.'))
    } catch {
      morphToast.error(t('common.error', 'Erro ao atualizar o projeto'))
    } finally {
      setBusy(false)
      setConfirmUpdate(false)
    }
  }

  async function handleSaveAsNewProject() {
    if (!showNewProjectForm) { setShowNewProjectForm(true); return }
    if (!newProjectName.trim() || !currentWorkspace || !currentProjectId) return

    setBusy(true)
    try {
      const meta = await getCurrentMetadata()
      const project = await projectApi.create(currentWorkspace.id, { name: newProjectName.trim() })
      await createCommitFromMetadata(project.id, meta, {
        name: t('projects.initialCommit', 'Projeto criado'),
        description: '',
      })
      onClose()
      navigate(`/projects/${project.id}/editor`)
      morphToast.success(t('conflict.savedAsNew', `Projeto "${project.name}" criado com sucesso!`))
    } catch (err) {
      openPlanDialogForError(err)
      morphToast.error(t('common.error', 'Erro ao criar projeto'))
    } finally {
      setBusy(false)
    }
  }

  function handleClose() {
    setConfirmUpdate(false)
    setShowNewProjectForm(false)
    setNewProjectName('')
    onClose()
  }

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title={t('conflict.title', 'Conflito de versão')}
      widthClassName="max-w-lg"
    >
      <div className="space-y-4">
        <p className="text-[13px]" style={{ color: 'var(--text-secondary, #9ca3af)' }}>
          {t(
            'conflict.description',
            'Outro usuário criou uma nova versão enquanto você editava. Escolha como proceder:',
          )}
        </p>

        {/* Update project */}
        <div
          className="rounded-lg border px-4 py-3 flex items-center justify-between gap-3"
          style={{ borderColor: confirmUpdate ? 'var(--color-danger-border)' : 'var(--border)', background: 'var(--bg-card)' }}
        >
          <div>
            <div className="text-[13px] font-medium">{t('conflict.updateProject', 'Atualizar projeto')}</div>
            <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
              {confirmUpdate
                ? t('conflict.updateConfirm', 'Suas alterações locais serão descartadas. Confirme clicando novamente.')
                : t('conflict.updateDesc', 'Descarta suas alterações e carrega a versão mais recente do servidor.')}
            </div>
          </div>
          <Button
            variant={confirmUpdate ? 'danger' : 'secondary'}
            size="sm"
            onClick={() => void handleUpdateProject()}
            disabled={busy}
          >
            {confirmUpdate ? t('conflict.confirmUpdate', 'Confirmar') : t('conflict.update', 'Atualizar')}
          </Button>
        </div>

        {/* Save as new project */}
        <div
          className="rounded-lg border px-4 py-3 space-y-2"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[13px] font-medium">{t('conflict.saveAsNew', 'Salvar como novo projeto')}</div>
              <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
                {t('conflict.saveAsNewDesc', 'Cria um novo projeto com suas alterações atuais.')}
              </div>
            </div>
            {!showNewProjectForm && (
              <Button variant="secondary" size="sm" onClick={() => setShowNewProjectForm(true)} disabled={busy}>
                {t('conflict.saveNew', 'Criar')}
              </Button>
            )}
          </div>
          {showNewProjectForm && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={t('conflict.newProjectName', 'Nome do novo projeto')}
                className="morph-input flex-1"
                disabled={busy}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleSaveAsNewProject() }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSaveAsNewProject()}
                disabled={busy || !newProjectName.trim()}
              >
                {t('common.create', 'Criar')}
              </Button>
            </div>
          )}
        </div>

        {/* Cancel */}
        <div className="flex justify-end pt-1">
          <Button variant="ghost" onClick={handleClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </ModalShell>
  )
}
